import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, XCircle, ShieldCheck, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { PdfViewerModal } from "@/components/PdfViewerModal";

export default function ValidarCertificado() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!!code);
  const [searchCode, setSearchCode] = useState(code || "");
  const [showPdf, setShowPdf] = useState(false);

  useEffect(() => {
    if (!code) { setLoading(false); return; }
    api.get(`/certificates/validate/${code}`)
      .then(r => setData(r.data))
      .catch(() => setData({ valid: false }))
      .finally(() => setLoading(false));
  }, [code]);

  // Função pública (sem autenticação) para baixar o PDF a partir do código de validação.
  // ago/2026 — atendendo pedido do gestor: quem receber o link do WhatsApp
  // deve poder baixar o próprio PDF do certificado direto na página de validação.
  const fetchPublicPdf = async (signal) => {
    return await fetch(`${API}/certificates/validate/${code}/download`, {
      signal,
    });
  };

  return (
    <div className="min-h-screen bg-hero-radial bg-white">
      <header className="max-w-[640px] mx-auto px-5 pt-8 pb-4 flex items-center justify-between">
        <Logo size={36} />
        <Link to="/login" className="text-xs text-[hsl(var(--accent))] hover:underline">Acessar plataforma</Link>
      </header>
      <main className="max-w-[640px] mx-auto px-5 pb-16">
        <div className="text-center mb-6">
          <ShieldCheck className="w-10 h-10 text-[hsl(var(--accent))] mx-auto" />
          <h1 className="font-display font-semibold text-2xl text-[hsl(var(--primary))] mt-2">Validação de Certificado</h1>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">Confirme a autenticidade de um certificado CEAP BRASIL.</p>
        </div>
        <Card className="rounded-2xl shadow-sm mb-4">
          <CardContent className="p-5">
            <form onSubmit={(e) => { e.preventDefault(); window.location.href = `/validar/${searchCode}`; }} className="flex gap-2">
              <Input placeholder="Código do certificado" value={searchCode} onChange={(e) => setSearchCode(e.target.value.toUpperCase())} data-testid="validate-input" />
              <Button type="submit" disabled={!searchCode}>Validar</Button>
            </form>
          </CardContent>
        </Card>

        {loading && <p className="text-center text-sm text-muted-foreground">Verificando…</p>}
        {!loading && code && (
          data?.valid ? (
            <Card className="rounded-2xl border-emerald-200 bg-emerald-50/40" data-testid="public-validation-status-valid">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 text-emerald-700 font-display font-semibold">
                  <CheckCircle2 className="w-6 h-6" /> Certificado válido
                </div>
                <Badge className="mt-3 bg-emerald-100 text-emerald-800 border-emerald-200" variant="outline">Emitido por CEAP BRASIL</Badge>
                <h2 className="mt-3 font-display font-semibold text-lg text-[hsl(var(--primary))]">{data.course_name}</h2>
                <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <div><dt className="text-muted-foreground">Aluno</dt><dd className="font-medium">{data.student_name}</dd></div>
                  <div><dt className="text-muted-foreground">Carga horária</dt><dd className="font-medium">{data.course_hours}h</dd></div>
                  <div><dt className="text-muted-foreground">Local</dt><dd className="font-medium">{data.course_city}</dd></div>
                  <div><dt className="text-muted-foreground">Data</dt><dd className="font-medium">{data.course_date}</dd></div>
                  <div className="col-span-2"><dt className="text-muted-foreground">Professor</dt><dd className="font-medium">{data.professor}</dd></div>
                  <div className="col-span-2"><dt className="text-muted-foreground">Código</dt><dd className="font-mono-id">{data.validation_code}</dd></div>
                </dl>

                {/* Botão de baixar PDF — abre o mesmo modal usado pelo aluno */}
                <div className="mt-5 pt-4 border-t border-emerald-200">
                  <Button
                    onClick={() => setShowPdf(true)}
                    className="w-full sm:w-auto"
                    data-testid="public-download-pdf"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Baixar certificado em PDF
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2">
                    O PDF é o mesmo emitido para o aluno, com todos os selos e QR code de autenticação.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="rounded-2xl border-rose-200 bg-rose-50/30" data-testid="public-validation-status-invalid">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 text-rose-700 font-display font-semibold">
                  <XCircle className="w-6 h-6" /> Certificado não encontrado
                </div>
                <p className="text-sm text-muted-foreground mt-2">Verifique o código digitado e tente novamente. Em caso de dúvida, contate o CEAP BRASIL.</p>
              </CardContent>
            </Card>
          )
        )}

        {/* Modal do PDF — reutiliza o mesmo componente do aluno */}
        {data?.valid && (
          <PdfViewerModal
            open={showPdf}
            onClose={() => setShowPdf(false)}
            title={`Certificado — ${data.course_name}`}
            courseName={data.course_name}
            filename={`certificado_${data.validation_code}.pdf`}
            validationUrl={typeof window !== "undefined" ? `${window.location.origin}/validar/${data.validation_code}` : undefined}
            fetchFn={fetchPublicPdf}
          />
        )}
      </main>
    </div>
  );
}
